import { Collapse as BlueprintCollapse } from '@blueprintjs/core';
import { AddLarge, ChevronDown, ChevronUp } from '@carbon/icons-react';
import * as React from 'react';
import {
  Body,
  Button,
  OutlinedIconButton,
  Tooltip,
} from 'vet-bones/design-system/components';

import { ProjectSidebarTileId } from 'src/constants/mosaic';
import {
  TEST_IDS,
  getProjectSidebarSectionTestId,
} from 'src/constants/testIds';
import { ProjectSidebarModelCard } from 'src/containers/ProjectSidebar/components/ProjectSidebarModelCard';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { modelResourceListSelector } from 'src/selectors/fullResourceList';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import styles from 'src/containers/ProjectDiagram/components/modelPopover/projectDiagramModelPopover.module.scss';

export const ProjectDiagramModelPopover: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();

  const targetModelId = useReduxSelector(
    projectTargetModelIdSelector(projectId)
  );

  const allItems = useReduxSelector(modelResourceListSelector(projectId));
  const selectedModelName =
    allItems.find((item) => item.value === targetModelId)?.text ||
    targetModelId;

  const addDisabled = useReduxSelector(isDraftDiagramSelector(projectId));

  const onAdd = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledProjectModelDialog({ isOpen: true }));
  }, [dispatch]);

  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div
      className={styles.popover}
      data-testid={getProjectSidebarSectionTestId(ProjectSidebarTileId.MODELS)}
    >
      <div className={styles.popoverHeader}>
        <div className={styles.popoverHeaderTitle}>
          <OutlinedIconButton
            className={styles.popoverHeaderToggle}
            elevated
            leftIcon={isOpen ? ChevronUp : ChevronDown}
            onClick={() => setIsOpen(!isOpen)}
            testId={TEST_IDS.projectSidebar.sectionToggleButton}
          />
          <Body className={styles.popoverHeaderLabel} size="md">
            {copy.components.projectDiagram.modelPopover.label}
          </Body>
          <Body className={styles.popoverHeaderName} ellipsize size="md">
            {selectedModelName}
          </Body>
        </div>
        <div className={styles.popoverHeaderActions}>
          <Tooltip
            description={
              copy.components.voicebox.disabledFor[ProjectSidebarTileId.MODELS]
            }
            disabled={!addDisabled}
          >
            <Button
              disabled={addDisabled}
              leftIcon={AddLarge}
              onClick={onAdd}
              size="xs"
              testId={TEST_IDS.projectSidebar.addButton}
              variant="tertiary-gray"
            />
          </Tooltip>
        </div>
      </div>
      <BlueprintCollapse isOpen={isOpen}>
        <div className={styles.popoverScroll}>
          <div className={styles.popoverItems}>
            {allItems.map((item, index) => (
              <ProjectSidebarModelCard
                key={item.value}
                idx={index}
                modelId={item.value}
              />
            ))}
          </div>
        </div>
      </BlueprintCollapse>
    </div>
  );
};
